import { useRef, useMemo } from 'react';
import { useOptions } from '../context/OptionsContext';
import client from '../api/client';

export default function useSupervisorItems({ items, setItems }) {
  const { glCodes, projects } = useOptions();
  const debounceTimers = useRef({});

  const GL_MAP = useMemo(() => {
    return Object.fromEntries(glCodes.map(g => [g.code, g.name]));
  }, [glCodes]);

  function handleItemFieldChange(itemId, field, value) {
    setItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item
    ));
    clearTimeout(debounceTimers.current[`${itemId}-${field}`]);
    debounceTimers.current[`${itemId}-${field}`] = setTimeout(async () => {
      try {
        await client.patch(`/api/receipt-items/${itemId}`, { [field]: value });
      } catch (err) {
        console.error('Item field update error:', err);
      }
    }, 500);
  }

  function handleGlCodeChange(itemId, glCode) {
    const glName = GL_MAP[glCode] || '';
    setItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, glCode, gl: glName } : item
    ));
    clearTimeout(debounceTimers.current[`${itemId}-glCode`]);
    clearTimeout(debounceTimers.current[`${itemId}-gl`]);
    debounceTimers.current[`${itemId}-glCode`] = setTimeout(async () => {
      try {
        await client.patch(`/api/receipt-items/${itemId}`, { glCode, gl: glName });
      } catch (err) {
        console.error('GL code update error:', err);
      }
    }, 500);
  }

  function handleTaskChange(itemId, taskId, taskName) {
    // Changing the tarea clears the CC: the previously-picked CC belonged to the old tarea's
    // filtered set and may not be valid for the new one. Mirrors UploadPage.handleTaskChange.
    // The preselect effect then refills it when the new tarea's set resolves to exactly one.
    setItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, selectedTaskId: taskId || null, selectedTaskName: taskName || null, costCenter: '' } : item
    ));
    clearTimeout(debounceTimers.current[`${itemId}-selectedTaskId`]);
    debounceTimers.current[`${itemId}-selectedTaskId`] = setTimeout(async () => {
      try {
        // Send costCenter: null explicitly so the backend's key-presence flag ('costCenter' in
        // req.body → V37.8) actually clears the column — COALESCE alone can't. Keep this a discrete
        // literal object: do NOT spread a wide item here, or unrelated keys would trip the flag.
        await client.patch(`/api/receipt-items/${itemId}`, { selectedTaskId: taskId || null, costCenter: null });
      } catch (err) {
        console.error('Task update error:', err);
      }
    }, 500);
  }

  function handleProjectChange(itemId, projectId) {
    const project = projects.find(p => String(p.id) === String(projectId));
    const projectName = project?.name || null;
    setItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, projectId: projectId || null, projectName } : item
    ));
    clearTimeout(debounceTimers.current[`${itemId}-projectId`]);
    debounceTimers.current[`${itemId}-projectId`] = setTimeout(async () => {
      try {
        await client.patch(`/api/receipt-items/${itemId}`, { projectId: projectId || null });
      } catch (err) {
        console.error('Project update error:', err);
      }
    }, 500);
  }

  // splitAmount + splitCostCenter are optional — used by the split modal to send actual values
  async function handleSplitItem(item, { splitAmount, splitCostCenter, parentId, origAmt } = {}) {
    const tempId = `temp-${Date.now()}`;
    const amt = splitAmount != null ? splitAmount : 0;
    const cc = splitCostCenter || item.costCenter || null;
    const parentItemId = parentId || item.id;
    const originalAmount = origAmt || item.originalAmount || parseFloat(item.amount) || 0;

    const newItem = {
      ...item,
      id: tempId,
      amount: amt,
      costCenter: cc,
      description: item.description || '',
      status: 'pending',
      glCode: item.glCode || null,
      gl: item.gl || null,
      projectId: item.projectId || null,
      projectName: item.projectName || null,
      selectedTaskId: item.selectedTaskId || null,
      selectedTaskName: item.selectedTaskName || null,
      taxRate: item.taxRate || 0,
      expenseType: item.expenseType || 'project',
      splitFromId: parentItemId,
      originalAmount,
    };
    setItems(prev => {
      const idx = prev.findIndex(i => i.id === item.id);
      const copy = [...prev];
      if (!item.originalAmount && idx >= 0) {
        copy[idx] = { ...copy[idx], originalAmount };
      }
      copy.splice(idx + 1, 0, newItem);
      return copy;
    });

    try {
      const res = await client.post('/api/receipt-items', {
        receiptId: item.receiptId,
        description: item.description || '',
        amount: amt,
        costCenter: cc,
        gl: item.gl || null,
        glCode: item.glCode || null,
        supervisorId: item.supervisorId,
        projectId: item.projectId || null,
        selectedTaskId: item.selectedTaskId || null,
        taxRate: item.taxRate || 0,
        quantity: item.quantity || 1,
        unit: item.unit || null,
        expenseType: item.expenseType || 'project',
        splitFromId: parentItemId,
        originalAmount,
      });
      setItems(prev => prev.map(i => {
        if (i.id !== tempId) return i;
        return {
          ...res.data,
          projectName: res.data.projectName || item.projectName,
          vendor: item.vendor,
          receiptDate: item.receiptDate,
          receiptNumber: item.receiptNumber,
          fileName: item.fileName,
          fileType: item.fileType,
          previewUrl: item.previewUrl,
          paymentMethod: item.paymentMethod,
          receiptAmount: item.receiptAmount,
          receiptStatus: item.receiptStatus,
          employeeName: item.employeeName,
          receiptUserId: item.receiptUserId,
        };
      }));
    } catch (err) {
      console.error('Split error:', err);
      setItems(prev => prev.filter(i => i.id !== tempId));
      alert('Error al dividir item: ' + (err.response?.data?.error || err.message));
    }
  }

  async function handleDeleteItem(item, group) {
    if (group.items.length <= 1) {
      alert('No se puede eliminar el único item del recibo.');
      return;
    }
    try {
      await client.delete(`/api/receipt-items/${item.id}`);
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (err) {
      console.error('Delete item error:', err);
      alert(err.response?.data?.error || 'Error al eliminar item.');
    }
  }

  async function handleAddItem(group) {
    const lastItem = group.items[group.items.length - 1];
    const tempId = `temp-${Date.now()}`;
    const newItem = {
      ...lastItem,
      id: tempId,
      amount: 0,
      description: '',
      status: 'pending',
      glCode: null,
      gl: null,
    };
    setItems(prev => {
      const lastIdx = prev.findIndex(i => i.id === lastItem.id);
      const copy = [...prev];
      copy.splice(lastIdx + 1, 0, newItem);
      return copy;
    });

    try {
      const res = await client.post('/api/receipt-items', {
        receiptId: group.receiptId,
        description: '',
        amount: 0,
        costCenter: null,
        gl: null,
        glCode: null,
        supervisorId: lastItem.supervisorId,
        projectId: lastItem.projectId || null,
      });
      setItems(prev => prev.map(i => {
        if (i.id !== tempId) return i;
        return {
          ...res.data,
          projectName: res.data.projectName || lastItem.projectName,
          vendor: lastItem.vendor,
          receiptDate: lastItem.receiptDate,
          receiptNumber: lastItem.receiptNumber,
          fileName: lastItem.fileName,
          fileType: lastItem.fileType,
          previewUrl: lastItem.previewUrl,
          paymentMethod: lastItem.paymentMethod,
          receiptAmount: lastItem.receiptAmount,
          receiptStatus: lastItem.receiptStatus,
          employeeName: lastItem.employeeName,
          receiptUserId: lastItem.receiptUserId,
        };
      }));
    } catch (err) {
      console.error('Add item error:', err);
      setItems(prev => prev.filter(i => i.id !== tempId));
      alert('Error al agregar item: ' + (err.response?.data?.error || err.message));
    }
  }

  function handleMoveItemToProject(item, direction, group) {
    const projectNames = [...new Set(group.items.map(i => i.projectName || '(Sin Proyecto)'))];
    const currentProjectName = item.projectName || '(Sin Proyecto)';
    const currentIdx = projectNames.indexOf(currentProjectName);
    const targetIdx = (currentIdx + direction + projectNames.length) % projectNames.length;
    const targetProjectName = projectNames[targetIdx];
    const targetItem = group.items.find(i => (i.projectName || '(Sin Proyecto)') === targetProjectName);
    const targetProjectId = targetItem?.projectId || null;
    handleProjectChange(item.id, targetProjectId || '');
  }

  return {
    GL_MAP,
    handleItemFieldChange,
    handleGlCodeChange,
    handleTaskChange,
    handleProjectChange,
    handleSplitItem,
    handleDeleteItem,
    handleAddItem,
    handleMoveItemToProject,
  };
}
